import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";

declare const YT: any;

interface YouTubePlayer {
  stopVideo: () => void;
  pauseVideo: () => void;
  playVideo: () => void;
  loadVideoById: (videoId: string) => void;
  setPlaybackQuality: (quality: string) => void;
  setVolume: (volume: number) => void;
  destroy: () => void;
}

@Component({
  standalone: true,
  selector: "app-musicthon-player",
  template: `<div class="musicthon-player-shell" [class.hidden]="!showVideo">
    <div #playerContainer class="musicthon-player"></div>
  </div>`,
  styleUrls: ["./musicthon-player.component.scss"],
  imports: [CommonModule],
})
export class MusicthonPlayerComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @ViewChild("playerContainer", { static: true })
  playerContainer!: ElementRef<HTMLDivElement>;

  @Input() showVideo = false;
  @Input() volume = 70;

  @Output() ended = new EventEmitter<void>();
  @Output() error = new EventEmitter<string>();
  @Output() ready = new EventEmitter<void>();
  @Output() playing = new EventEmitter<void>();

  private player: YouTubePlayer | null = null;
  private pendingVideoId: string | null = null;
  private isReady = false;
  private lastVideoId: string | null = null;
  private rebuildAttempts = 0;
  private static ytApiPromise: Promise<void> | null = null;

  async ngAfterViewInit(): Promise<void> {
    await this.ensureYouTubeApi();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["volume"]) {
      this.applyVolume();
    }
  }

  ngOnDestroy(): void {
    this.destroyPlayer();
  }

  async play(videoId: string) {
    if (!videoId) return;
    await this.ensureYouTubeApi();
    if (this.lastVideoId !== videoId) {
      this.lastVideoId = videoId;
      this.rebuildAttempts = 0;
    }
    if (!this.player) {
      this.pendingVideoId = videoId;
      this.createPlayer(videoId);
      return;
    }
    if (!this.isReady) {
      this.pendingVideoId = videoId;
      return;
    }
    try {
      this.player.loadVideoById(videoId);
    } catch (e) {
      if (this.rebuildAttempts >= 1) {
        console.warn("[MusicthonPlayer] rebuild limit reached", e);
        this.error.emit("rebuild_failed");
        return;
      }
      this.rebuildAttempts += 1;
      console.warn("[MusicthonPlayer] loadVideoById failed, rebuilding", e);
      this.rebuildPlayer(videoId);
    }
  }

  stop() {
    if (this.player) {
      try {
        this.player.stopVideo();
      } catch (e) {
        console.warn("[MusicthonPlayer] stopVideo failed", e);
      }
    }
  }

  pause() {
    if (this.player) {
      try {
        this.player.pauseVideo();
      } catch (e) {
        console.warn("[MusicthonPlayer] pauseVideo failed", e);
      }
    }
  }

  resume() {
    if (this.player) {
      try {
        this.player.playVideo();
      } catch (e) {
        console.warn("[MusicthonPlayer] playVideo failed", e);
      }
    }
  }

  private createPlayer(initialVideoId?: string) {
    const origin = typeof window !== "undefined" ? window.location.origin : undefined;

    this.player = new YT.Player(this.playerContainer.nativeElement, {
      height: "100%",
      width: "100%",
      host: "https://www.youtube.com",
      events: {
        onReady: () => {
          this.isReady = true;
          this.ready.emit();
          this.applyVolume();
          const nextVideo = this.pendingVideoId || initialVideoId;
          this.pendingVideoId = null;
          if (nextVideo && this.player) {
            try {
              this.player.setPlaybackQuality("tiny");
              this.player.loadVideoById(nextVideo);
              return;
            } catch (e) {
              console.warn("[MusicthonPlayer] loadVideoById failed", e);
            }
          }
          try {
            this.player?.playVideo();
          } catch (e) {
            console.warn("[MusicthonPlayer] playVideo onReady failed", e);
          }
        },
        onStateChange: (event: any) => {
          if (event.data === YT.PlayerState.PLAYING) {
            try {
              this.player?.setPlaybackQuality("tiny");
              this.applyVolume();
            } catch (e) {
              console.warn("[MusicthonPlayer] setPlaybackQuality failed", e);
            }
            this.playing.emit();
          }
          // YT.PlayerState.ENDED === 0
          if (event.data === YT.PlayerState.ENDED) {
            this.ended.emit();
          }
        },
        onError: (event: any) => {
          this.error.emit(event?.data?.toString() ?? "unknown_error");
        },
      },
      playerVars: {
        autoplay: 1,
        controls: 0,
        disablekb: 1,
        modestbranding: 1,
        rel: 0,
        playsinline: 1,
        origin,
        vq: "tiny",
      },
      videoId: initialVideoId,
    });
  }

  private rebuildPlayer(nextVideoId: string) {
    this.pendingVideoId = nextVideoId;
    this.destroyPlayer();
    this.createPlayer(nextVideoId);
  }

  private applyVolume() {
    const clamped = Math.max(0, Math.min(100, Number(this.volume)));
    if (Number.isNaN(clamped)) return;
    if (!this.player || !this.isReady) return;
    try {
      this.player.setVolume(clamped);
    } catch (e) {
      console.warn("[MusicthonPlayer] setVolume failed", e);
    }
  }

  private destroyPlayer() {
    if (this.player && typeof this.player.destroy === "function") {
      this.player.destroy();
    }
    this.player = null;
    this.isReady = false;
    this.pendingVideoId = null;
    // limpa o container para garantir que um novo iframe seja criado limpo
    if (this.playerContainer?.nativeElement) {
      this.playerContainer.nativeElement.innerHTML = "";
    }
  }

  private async ensureYouTubeApi(): Promise<void> {
    if (MusicthonPlayerComponent.ytApiPromise) {
      return MusicthonPlayerComponent.ytApiPromise;
    }

    MusicthonPlayerComponent.ytApiPromise = new Promise((resolve) => {
      // Já existe?
      if (typeof window !== "undefined" && (window as any).YT?.Player) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.body.appendChild(script);

      (window as any).onYouTubeIframeAPIReady = () => {
        resolve();
      };
    });

    return MusicthonPlayerComponent.ytApiPromise;
  }
}
